/**
 * CPU-side palettes referenced per splat by index.
 *
 * Every splat carries a transform index and a colour index (0 = identity in
 * both). Moving or grading a selection allocates new palette entries rather
 * than rewriting splat data, so edits stay cheap, exactly undoable and are only
 * baked into the gaussians on save. The renderer mirrors `data` into GPU
 * textures whenever `version` changes.
 *
 * Allocation is a stack: an op allocates on do() and frees on undo(), and the
 * history guarantees LIFO order, so redo re-allocates the same indices.
 */
import { createGradeTerms, gradeFromRows, gradeRows, type GradeTerms } from './ColorGrade';

/** Column-major Mat4 layout (PlayCanvas Mat4.data) → 3x4 row-major palette layout. */
const MAT4_TO_ROWS = [0, 4, 8, 12, 1, 5, 9, 13, 2, 6, 10, 14];

export const TRANSFORM_FLOATS = 12;
export const COLOR_FLOATS = 16;

abstract class Palette {
    readonly stride: number;
    data: Float32Array;
    count = 1;
    version = 0;

    constructor(stride: number) {
        this.stride = stride;
        this.data = new Float32Array(stride * 64);
        this.writeIdentity(0);
    }

    /** Allocate `num` consecutive entries initialised to identity; returns the first index. */
    alloc(num = 1): number {
        const first = this.count;
        this.count += num;
        this.ensureCapacity(this.count);
        for (let i = 0; i < num; i++) this.writeIdentity(first + i);
        this.version++;
        return first;
    }

    /** Release the last `num` entries (stack order). */
    free(num: number): void {
        this.count = Math.max(1, this.count - num);
        this.version++;
    }

    reset(): void {
        this.count = 1;
        this.version++;
    }

    protected ensureCapacity(entries: number): void {
        const needed = entries * this.stride;
        if (needed <= this.data.length) return;
        let size = this.data.length;
        while (size < needed) size *= 2;
        const next = new Float32Array(size);
        next.set(this.data);
        this.data = next;
    }

    protected abstract writeIdentity(index: number): void;
}

export class TransformPalette extends Palette {
    constructor() {
        super(TRANSFORM_FLOATS);
    }

    protected writeIdentity(index: number): void {
        const o = index * TRANSFORM_FLOATS;
        this.data.fill(0, o, o + TRANSFORM_FLOATS);
        this.data[o] = 1;
        this.data[o + 5] = 1;
        this.data[o + 10] = 1;
    }

    /** Store a model-space affine transform (column-major 16-float Mat4 data). */
    setMatrix(index: number, mat4: ArrayLike<number>): void {
        const o = index * TRANSFORM_FLOATS;
        for (let i = 0; i < TRANSFORM_FLOATS; i++) this.data[o + i] = mat4[MAT4_TO_ROWS[i]];
        this.version++;
    }

    /** Read an entry back as column-major 16-float Mat4 data. */
    getMatrix(index: number, out: Float32Array | number[] = new Float32Array(16)): Float32Array | number[] {
        const o = index * TRANSFORM_FLOATS;
        for (let i = 0; i < TRANSFORM_FLOATS; i++) out[MAT4_TO_ROWS[i]] = this.data[o + i];
        out[3] = 0; out[7] = 0; out[11] = 0; out[15] = 1;
        return out;
    }

    isIdentity(index: number): boolean {
        if (index === 0) return true;
        const o = index * TRANSFORM_FLOATS;
        const d = this.data;
        return d[o] === 1 && d[o + 1] === 0 && d[o + 2] === 0 && d[o + 3] === 0
            && d[o + 4] === 0 && d[o + 5] === 1 && d[o + 6] === 0 && d[o + 7] === 0
            && d[o + 8] === 0 && d[o + 9] === 0 && d[o + 10] === 1 && d[o + 11] === 0;
    }
}

const rowsScratch = new Float32Array(12);

export class ColorPalette extends Palette {
    constructor() {
        super(COLOR_FLOATS);
    }

    protected writeIdentity(index: number): void {
        const o = index * COLOR_FLOATS;
        this.data.fill(0, o, o + COLOR_FLOATS);
        this.data[o] = 1;
        this.data[o + 5] = 1;
        this.data[o + 10] = 1;
        this.data[o + 12] = 1; // transparency
    }

    setTerms(index: number, terms: GradeTerms): void {
        gradeRows(terms, rowsScratch);
        const o = index * COLOR_FLOATS;
        this.data.set(rowsScratch, o);
        this.data[o + 12] = terms.transparency;
        this.data[o + 13] = 0;
        this.data[o + 14] = 0;
        this.data[o + 15] = 0;
        this.version++;
    }

    getTerms(index: number, out: GradeTerms = createGradeTerms()): GradeTerms {
        const o = index * COLOR_FLOATS;
        return gradeFromRows(this.data.subarray(o, o + 12), this.data[o + 12], out);
    }

    isIdentity(index: number): boolean {
        if (index === 0) return true;
        const o = index * COLOR_FLOATS;
        const d = this.data;
        return d[o] === 1 && d[o + 1] === 0 && d[o + 2] === 0 && d[o + 3] === 0
            && d[o + 4] === 0 && d[o + 5] === 1 && d[o + 6] === 0 && d[o + 7] === 0
            && d[o + 8] === 0 && d[o + 9] === 0 && d[o + 10] === 1 && d[o + 11] === 0
            && d[o + 12] === 1;
    }
}
